import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Music2, Loader2, Mic2, Headphones, MailCheck } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "beatmaker" ? "beatmaker" : "artist");
  const [form, setForm] = useState({ pseudo: "", email: "", password: "", artist_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // holds {email, role} after success
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await register({ ...form, role });
      toast.success(`Compte créé, bienvenue @${u.pseudo} !`);
      const pending = localStorage.getItem("ilm_pending_search");
      if (pending && role === "artist") {
        try {
          const { data } = await api.post("/searches", JSON.parse(pending));
          localStorage.removeItem("ilm_pending_search");
          nav(`/app/search/${data.id}`, { replace: true });
          return;
        } catch { localStorage.removeItem("ilm_pending_search"); }
      }
      nav(role === "beatmaker" ? "/app/feed" : "/app", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (sent) {
    const resend = async () => {
      try { await api.post("/auth/resend-verification"); toast.success("Email renvoyé ✉️"); }
      catch { toast.error("Erreur"); }
    };
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-violet-600/20 rounded-full blur-3xl" />
        <div className="w-full max-w-md relative text-center animate-fade-up">
          <div className="bg-[#0B0716] border border-zinc-800 rounded-3xl p-8" data-testid="register-check-email">
            <div className="w-16 h-16 rounded-full bg-violet-500/15 flex items-center justify-center mx-auto mb-4"><MailCheck size={30} className="text-violet-400" /></div>
            <h1 className="text-2xl font-display font-bold mb-2">Vérifie ta boîte mail 📩</h1>
            <p className="text-sm text-zinc-400 mb-1">Un email de confirmation a été envoyé à</p>
            <p className="text-sm font-semibold text-violet-300 mb-5">{sent.email}</p>
            <p className="text-xs text-zinc-500 mb-6">Clique sur le lien pour activer ton compte et pouvoir {sent.role === "artist" ? "publier des recherches" : "proposer tes prods"}.</p>
            <button onClick={() => nav(sent.role === "beatmaker" ? "/app/feed" : "/app", { replace: true })} data-testid="continue-to-app-button"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold transition-all mb-3">
              Continuer vers l'app
            </button>
            <button onClick={resend} data-testid="resend-email-button" className="text-sm text-zinc-400 hover:text-white">Renvoyer l'email</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-violet-600/20 rounded-full blur-3xl" />
      <div className="w-full max-w-md relative animate-fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center glow-violet">
            <Music2 size={22} className="text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">ilove<span className="gradient-text">muzik</span></span>
        </Link>
        <div className="bg-[#0B0716] border border-zinc-800 rounded-3xl p-7">
          <h1 className="text-2xl font-display font-bold mb-1">Créer un compte</h1>
          <p className="text-sm text-zinc-400 mb-5">Choisis ton type de compte</p>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <button type="button" onClick={() => setRole("artist")} data-testid="register-role-artist"
              className={`p-4 rounded-2xl border text-left transition-all ${role === "artist" ? "border-violet-500 bg-violet-600/15" : "border-zinc-700 bg-zinc-900"}`}>
              <Mic2 size={22} className={role === "artist" ? "text-violet-400" : "text-zinc-400"} />
              <p className="font-semibold mt-2">Artiste</p>
              <p className="text-xs text-zinc-400">Je cherche des prods</p>
            </button>
            <button type="button" onClick={() => setRole("beatmaker")} data-testid="register-role-beatmaker"
              className={`p-4 rounded-2xl border text-left transition-all ${role === "beatmaker" ? "border-violet-500 bg-violet-600/15" : "border-zinc-700 bg-zinc-900"}`}>
              <Headphones size={22} className={role === "beatmaker" ? "text-violet-400" : "text-zinc-400"} />
              <p className="font-semibold mt-2">Beatmaker</p>
              <p className="text-xs text-zinc-400">Je propose des prods</p>
            </button>
          </div>

          {err && <div className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2" data-testid="register-error">{err}</div>}
          <form onSubmit={submit} className="space-y-3.5">
            <input data-testid="register-pseudo" required value={form.pseudo} onChange={set("pseudo")} placeholder="Pseudo"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            {role === "artist" && (
              <input data-testid="register-artistname" value={form.artist_name} onChange={set("artist_name")} placeholder="Nom d'artiste (optionnel)"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            )}
            <input data-testid="register-email" required type="email" value={form.email} onChange={set("email")} placeholder="Email"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            <input data-testid="register-password" required type="password" value={form.password} onChange={set("password")} placeholder="Mot de passe"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            <button data-testid="register-submit" disabled={busy} type="submit"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold transition-all disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} Créer mon compte
            </button>
          </form>
          <p className="text-sm text-zinc-400 text-center mt-5">
            Déjà un compte ? <Link to="/login" className="text-violet-400 font-medium">Connexion</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
